import React, { useEffect, useState } from 'react'
import {
  ActivityIndicator,
  Image,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { KeyboardAwareView, SearchBar, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { showSuccessToast } from '@utils'
import { useCurrentUserStore } from '@store'
import { iconApp } from '@assets'
import { ScreenProps } from '@navigation'
import {
  EVENTS_QUERY_KEY,
  type EventAttendee,
  useEventAttendees,
  useEventByCode,
  usePublicEvent,
  useScreen,
  useConnectWithAttendee,
  useDeleteContact,
  useEventsPullToRefresh,
  useRefreshEventsOnFocus,
} from '@hooks'
import { EventHero } from '../components/EventHero'
import { AttendeeTeaser } from '../components/AttendeeTeaser'
import { AttendeeCard } from '../components/AttendeeCard'
import { JoinPanel } from '../components/JoinPanel'
import { ViewerPanel } from '../components/ViewerPanel'

/**
 * `/e/:code` — the page behind the QR code and the invitation links.
 * Works logged out (public teaser + sign-up) and logged in (join in one click,
 * registered status, attendee list, organizer shortcut).
 */
export const PublicEvent: React.FC<ScreenProps<'PublicEvent'>> = ({ route, navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isDesktop } = useScreen()
  const styles = getStyles(theme, isDesktop)
  const code = String(route.params?.code ?? '')
    .trim()
    .toUpperCase()
  const isLoggedIn = !!useCurrentUserStore((state) => state.userID)
  const { refreshing, onRefresh } = useEventsPullToRefresh()
  // Reopened later (link, back from payment…): never show a stale phase or list.
  useRefreshEventsOnFocus()

  // Back from the payment page: confirm once, then drop the parameter.
  const paymentResult = route.params?.payment
  useEffect(() => {
    if (paymentResult !== 'success') return
    showSuccessToast(t('events.payment.successTitle'), t('events.payment.successMessage'))
    navigation.setParams({ payment: undefined })
  }, [navigation, paymentResult, t])

  const detailQuery = useEventByCode(code, { enabled: isLoggedIn })
  const detail = detailQuery.data
  const role = detail?.viewer.role ?? 'none'
  const canSeeList = role === 'attendee' || role === 'organizer'
  // Logged out, or logged in but not registered (yet): the public view — faces
  // and first names only. Registered people and the organizer get the full list.
  const publicQuery = usePublicEvent(code, {
    enabled: !isLoggedIn || (detailQuery.isSuccess && !canSeeList),
  })
  const event = detail?.event ?? publicQuery.data?.event
  const organizer = detail?.organizer ?? publicQuery.data?.organizer ?? null

  const [search, setSearch] = useState('')
  // "Étaient présents" is the default for people arriving from the recap email
  // (?filtre=presents); it only exists if the organizer used the check-in.
  const [presentOnly, setPresentOnly] = useState(route.params?.filtre === 'presents')
  const attendeesQuery = useEventAttendees(
    event?.id,
    search,
    canSeeList,
    presentOnly ? 'present' : 'all',
  )
  // Registered people (and the organizer) see full profiles as soon as they are in.
  const fullProfiles = canSeeList
  const checkInUsed = attendeesQuery.data?.metadata.checkInUsed === true
  // Nobody was ticked at the door: a "?filtre=presents" link falls back to everyone.
  const checkInUnused = attendeesQuery.data?.metadata.checkInUsed === false
  useEffect(() => {
    if (presentOnly && checkInUnused) setPresentOnly(false)
  }, [presentOnly, checkInUnused])

  const queryClient = useQueryClient()
  const connect = useConnectWithAttendee(event?.id, event?.name ?? '')
  const removeContact = useDeleteContact()
  const [addingId, setAddingId] = useState<string | null>(null)
  const addContact = (attendee: EventAttendee) => {
    if (!attendee.accountId) return
    setAddingId(attendee.attendeeId)
    connect.mutate(
      { recipientAccountId: attendee.accountId },
      {
        onSettled: () => {
          setAddingId(null)
          void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
        },
      },
    )
  }

  const removeFromContacts = (attendee: EventAttendee) => {
    if (!attendee.accountId) return
    removeContact.mutate(
      { id: attendee.accountId },
      { onSettled: () => void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] }) },
    )
  }

  const isLoading = isLoggedIn ? detailQuery.isLoading : publicQuery.isLoading
  const failed = isLoggedIn ? detailQuery.isError : publicQuery.isError

  const brandBar = !isLoggedIn ? (
    <View style={styles.brandBar}>
      <Image source={iconApp} style={styles.brandLogo} />
      <Text type="headline" color={theme.colors.primary}>
        {t('welcome.title')}
      </Text>
    </View>
  ) : null

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.colors.primary} />
      </View>
    )
  }

  if (failed || !event) {
    return (
      <View style={styles.page}>
        {brandBar}
        <View style={styles.center}>
          <Text type="titleBold" center>
            {t('events.public.notFound')}
          </Text>
          <Text type="body" center color={theme.colors.greyBlue}>
            {t('events.public.notFoundHint')}
          </Text>
        </View>
      </View>
    )
  }

  const teaser = canSeeList
    ? (attendeesQuery.data?.data ?? []).map((a) => ({
        firstName: a.firstName,
        avatarUrl: a.avatarUrl,
        isSelf: a.isSelf,
      }))
    : (publicQuery.data?.attendees ?? [])

  const attendeesSection = (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text type="titleBold">
          {fullProfiles ? t('events.public.whoCame') : t('events.public.whoIsComing')}
        </Text>
        {fullProfiles ? <SearchBar value={search} onChangeText={setSearch} hasMaxWidth /> : null}
      </View>
      {fullProfiles && checkInUsed ? (
        <View style={styles.filters}>
          {[false, true].map((value) => (
            <TouchableOpacity
              key={String(value)}
              accessibilityRole="button"
              accessibilityState={{ selected: presentOnly === value }}
              testID={value ? 'filter-present' : 'filter-all'}
              onPress={() => setPresentOnly(value)}
              style={[styles.filter, presentOnly === value && styles.filterActive]}
            >
              <Text
                type="bodySmallBold"
                color={presentOnly === value ? theme.colors.white : theme.colors.secondary}
              >
                {value ? t('events.public.filterPresent') : t('events.public.filterAll')}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
      {fullProfiles ? (
        <Text type="bodySmall" color={theme.colors.greyBlue}>
          {t('events.relation.meetingPlaceNote', { event: event.name })}
        </Text>
      ) : null}
      {fullProfiles && attendeesQuery.isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : fullProfiles && (attendeesQuery.data?.data ?? []).length === 0 ? (
        <Text type="body" color={theme.colors.greyBlue}>
          {search ? t('events.manage.attendees.emptySearch') : t('events.public.nobodyYet')}
        </Text>
      ) : fullProfiles ? (
        <View style={styles.cards}>
          {(attendeesQuery.data?.data ?? []).map((attendee) => (
            <View key={attendee.attendeeId} style={styles.cardCell}>
              <AttendeeCard
                attendee={attendee}
                onAdd={addContact}
                onRemove={removeFromContacts}
                isAdding={addingId === attendee.attendeeId}
              />
            </View>
          ))}
        </View>
      ) : (
        <>
          <AttendeeTeaser attendees={teaser} total={event.attendeeCount} />
          {event.attendeeCount > 0 ? (
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t('events.public.teaserHint')}
            </Text>
          ) : null}
        </>
      )}
    </View>
  )

  const sidePanel =
    isLoggedIn && detail ? (
      <ViewerPanel
        detail={detail}
        onManage={() =>
          navigation.navigate('Menu', {
            screen: 'Events',
            // Nested stack params are untyped in MenuStackParamList (same as Notes, Categories…).
            params: { screen: 'EventManage', params: { eventId: event.id } },
          } as never)
        }
        onViewTicket={() => navigation.navigate('MyTicket', { eventId: event.id })}
      />
    ) : (
      <JoinPanel event={event} />
    )

  return (
    <KeyboardAwareView isFullScreen androidOnly offsetTopInset>
      <ScrollView
        style={styles.page}
        contentContainerStyle={styles.scroll}
        // iOS: the sign-up / code fields and their button stay above the keyboard,
        // and a tap on a button lands even while the keyboard is up.
        keyboardShouldPersistTaps="handled"
        automaticallyAdjustKeyboardInsets
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.colors.primary}
          />
        }
        testID="public-event-scroll"
      >
        {brandBar}
        <View style={styles.layout}>
          {isDesktop ? (
            <>
              <View style={styles.main}>
                <EventHero event={event} organizer={organizer} />
                {attendeesSection}
              </View>
              <View style={styles.side}>{sidePanel}</View>
            </>
          ) : (
            // Phone (the QR-code case): the sign-up comes right after the event
            // card, before the attendee list, so nobody has to scroll to join.
            <>
              <EventHero event={event} organizer={organizer} />
              {sidePanel}
              {attendeesSection}
            </>
          )}
        </View>
      </ScrollView>
    </KeyboardAwareView>
  )
}

const getStyles = (theme: Theme, isDesktop: boolean) =>
  StyleSheet.create({
    page: { flex: 1, backgroundColor: theme.colors.pageBackground },
    scroll: { paddingBottom: theme.spacing.xxxl * 2, alignItems: 'center' },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.xxl,
      gap: theme.spacing.sm,
    },
    brandBar: {
      width: '100%',
      maxWidth: 1120,
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.xl,
      paddingTop: theme.spacing.xl,
      gap: theme.spacing.sm,
    },
    brandLogo: { width: 32, height: 32, borderRadius: 16 },
    layout: {
      width: '100%',
      maxWidth: 1120,
      paddingHorizontal: isDesktop ? theme.spacing.xl : theme.spacing.lg,
      paddingTop: theme.spacing.xl,
      flexDirection: isDesktop ? 'row' : 'column',
      alignItems: isDesktop ? 'flex-start' : 'stretch',
      gap: theme.spacing.xxl,
    },
    main: {
      flex: isDesktop ? 1 : undefined,
      width: isDesktop ? undefined : '100%',
      gap: theme.spacing.xxl,
      minWidth: 0,
    },
    side: {
      width: isDesktop ? 380 : '100%',
      ...(isDesktop && Platform.OS === 'web'
        ? ({ position: 'sticky', top: theme.spacing.xl } as object)
        : {}),
    },
    section: { gap: theme.spacing.lg },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing.md,
    },
    filters: { flexDirection: 'row', gap: theme.spacing.xs },
    filter: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs + 2,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    filterActive: { backgroundColor: theme.colors.primary, borderColor: theme.colors.primary },
    cards: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.md },
    cardCell: { width: isDesktop ? '48.5%' : '100%' },
  })
